import React, { useEffect, useRef, useState } from "react";
import { Share2, Link2, Twitter, Facebook, Linkedin, Mail, MessageCircle, Check, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import { cn } from "../lib/utils";

export default function ShareButton({ title, text, className }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);

  const url = typeof window !== "undefined" ? window.location.href : "";
  const shareText = text || title || "Discover the majesty of African history on Our African Narratives.";

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const onTrigger = () => setOpen((o) => !o);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied — ready to share");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy link");
    }
    setOpen(false);
  };

  const enc = encodeURIComponent;
  const links = [
    { icon: GraduationCap, label: "Google Classroom", href: `https://classroom.google.com/share?url=${enc(url)}&title=${enc(title || "Our African Narratives")}&body=${enc(shareText)}` },
    { icon: Twitter, label: "X / Twitter", href: `https://twitter.com/intent/tweet?text=${enc(shareText)}&url=${enc(url)}` },
    { icon: Facebook, label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}` },
    { icon: MessageCircle, label: "WhatsApp", href: `https://wa.me/?text=${enc(shareText + " " + url)}` },
    { icon: Linkedin, label: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}` },
    { icon: Mail, label: "Email", href: `mailto:?subject=${enc(title || "Our African Narratives")}&body=${enc(shareText + "\n\n" + url)}` },
  ];

  return (
    <div className={cn("relative", className)} ref={ref}>
      <button
        onClick={onTrigger}
        data-testid="share-btn"
        className="inline-flex items-center gap-2 border border-edge px-3 py-2 text-xs text-muted transition-colors hover:border-gold hover:text-gold"
      >
        <Share2 className="h-4 w-4" /> Share
      </button>

      {open && (
        <div
          className="absolute right-0 z-40 mt-2 w-56 border border-edge bg-ash p-1 shadow-2xl shadow-black/50"
          data-testid="share-menu"
        >
          <button
            onClick={copyLink}
            data-testid="share-copy-link"
            className="flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm text-bone transition-colors hover:bg-ink hover:text-gold"
          >
            {copied ? <Check className="h-4 w-4 text-gold" /> : <Link2 className="h-4 w-4" />}
            {copied ? "Copied!" : "Copy link"}
          </button>
          <div className="my-1 h-px bg-edge" />
          {links.map((l) => (
            <a
              key={l.label}
              href={l.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              data-testid={`share-${l.label.toLowerCase().split(" ")[0].replace("/", "")}`}
              className="flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm text-bone transition-colors hover:bg-ink hover:text-gold"
            >
              <l.icon className="h-4 w-4" /> {l.label}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
